import React, { useCallback, useRef } from 'react';

interface IParams {
  getTarget: () => HTMLElement | null;
  minHeight: number;
  maxHeight: number;
}

interface IDragState {
  pointerId: number;
  startY: number;
  startHeight: number;
}

interface IVerticalResizeHandleProps {
  onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerMove: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerUp: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerCancel: (event: React.PointerEvent<HTMLElement>) => void;
  onLostPointerCapture: (event: React.PointerEvent<HTMLElement>) => void;
}

const PRIMARY_BUTTON = 0;

export default function useVerticalResize({ getTarget, minHeight, maxHeight }: IParams): IVerticalResizeHandleProps {
  const dragRef = useRef<IDragState | null>(null);

  const clampHeight = useCallback(
    (rawHeight: number) => Math.min(Math.max(minHeight, maxHeight), Math.max(minHeight, rawHeight)),
    [minHeight, maxHeight],
  );

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (event.button !== PRIMARY_BUTTON || dragRef.current) {
        return;
      }
      const target = getTarget();
      if (!target) {
        return;
      }
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      dragRef.current = { pointerId: event.pointerId, startY: event.clientY, startHeight: target.offsetHeight };
    },
    [getTarget],
  );

  const onPointerMove = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      const drag = dragRef.current;
      const target = getTarget();
      if (!drag || drag.pointerId !== event.pointerId || !target) {
        return;
      }
      target.style.height = `${clampHeight(drag.startHeight + event.clientY - drag.startY)}px`;
    },
    [getTarget, clampHeight],
  );

  const endDrag = useCallback((event: React.PointerEvent<HTMLElement>) => {
    if (!dragRef.current || dragRef.current.pointerId !== event.pointerId) {
      return;
    }
    dragRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp: endDrag,
    onPointerCancel: endDrag,
    onLostPointerCapture: endDrag,
  };
}
